/**
 * @component KpiCard
 * @description Erweiterte KPI-Karte (UI-Bibliothek) mit Trend-Anzeige,
 * Icon, Tooltip und optionalem Klick-Handler.
 * Trend wird als farbiger Badge mit Pfeil-Icon dargestellt.
 *
 * @prop {string}        label      - Bezeichnung der Kennzahl
 * @prop {string|number} value      - Anzuzeigender Wert
 * @prop {string}        [trend]    - Trend-Text (z.B. "+12%")
 * @prop {boolean}       [trendUp]  - Ob Trend positiv (gruen) oder negativ (rot) ist
 * @prop {Component}     [icon]     - Optionales Icon-Component
 * @prop {string}        [color]    - Hintergrundfarbe des Icon-Containers
 * @prop {string}        [tooltip]  - Optionaler Tooltip-Text bei Hover
 * @prop {object}        [valueStyle] - Optionale Inline-Styles fuer den Wert
 * @usage Verwendet auf Home, Operations, Forecast und weiteren KPI-Seiten
 */
import { useState } from 'react';
import { TrendingUp, TrendingDown } from 'lucide-react';

export function KpiCard({ label, value, trend, trendUp, icon: Icon, color, className = '', valueStyle, tooltip, ...props }) {
  const clickable = !!props.onClick;
  const [showTip, setShowTip] = useState(false);
  return (
    <div
      className={`bg-surface-card border border-line-card rounded-card shadow-card px-6 py-5 relative ${clickable ? 'cursor-pointer hover:shadow-card-hover hover:border-line-hover transition-all' : ''} ${className}`}
      onMouseEnter={() => tooltip && setShowTip(true)}
      onMouseLeave={() => setShowTip(false)}
      {...props}
    >
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <p className="text-[11px] font-bold text-content-secondary uppercase tracking-wider truncate">
            {label}
            {tooltip && <span className="ml-1 text-content-tertiary cursor-help text-[10px]">&#x2139;&#xFE0F;</span>}
          </p>
          <p className="text-[26px] font-bold tracking-tight text-content mt-1" style={valueStyle}>{value}</p>
          {trend && (
            <div className={`inline-flex items-center gap-1 mt-2 px-2 py-0.5 rounded-lg text-[12px] font-semibold ${trendUp === false ? 'bg-red-500/10 text-red-500' : trendUp ? 'bg-emerald-500/10 text-emerald-500' : 'text-content-secondary'}`}>
              {trendUp === true && <TrendingUp className="w-3.5 h-3.5" />}
              {trendUp === false && <TrendingDown className="w-3.5 h-3.5" />}
              {trend}
            </div>
          )}
        </div>
        {Icon && (
          <div className={`p-2 rounded-button shrink-0 ${color || 'bg-[#EBF4FD] text-[#338edc]'}`}>
            <Icon className="w-5 h-5" />
          </div>
        )}
      </div>
      {showTip && tooltip && (
        <div className="absolute z-50 left-0 top-full mt-1 bg-surface-elevated border border-line rounded-card shadow-2xl p-2.5 text-[10px] text-content-secondary whitespace-pre-line min-w-[180px]"
          style={{ pointerEvents: 'none' }}>
          {tooltip}
        </div>
      )}
    </div>
  );
}
